// WO-56 — the two live AI chat assistants, on every page inside the app.
//
// MATT · AI support assistant   — how to use Mid2Bid (answers only from the support knowledge).
// ANTHONY · AI market assistant — GCC market questions (answers only from the four Mid2Bid
//                                  databases as this person sees them, plus up to 2 reports).
// Both are clearly labelled as AI (EU AI Act art. 50(1)). Nothing is kept in the browser:
// the conversation lives only while the page is open. The server decides who sees which
// assistant and how many messages are left; this component only shows it.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, X, Send, Bot, FileText, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { pathId } from '../../lib/pathId';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COPY = {
  en: {
    open: 'Open the AI assistants',
    close: 'Close',
    support: 'Support',
    market: 'Market',
    supportName: 'MATT · AI support assistant',
    marketName: 'ANTHONY · AI market assistant',
    aiNotice: 'You are chatting with an AI, not a person. Answers are generated automatically and can be wrong — check important facts.',
    supportHello: 'Hi! I can help you use Mid2Bid: sign-in, plans, searching, alerts, the CRM, reports… What do you need?',
    marketHello: 'Hi! Ask me about GCC tenders, projects, companies and market news. I answer only from the Mid2Bid databases you can see, and from our market reports.',
    placeholder: 'Type your question…',
    send: 'Send',
    thinking: 'Thinking…',
    leftDay: (n) => `${n} message${n === 1 ? '' : 's'} left today`,
    leftMonth: (n) => `${n} question${n === 1 ? '' : 's'} left this month`,
    records: 'Records',
    reports: 'Market reports',
    owned: 'In your library',
    notOwned: 'Available to buy',
    upgrade: 'See plans',
    error: 'The assistant could not answer. Please try again in a moment.',
    privacy: 'Conversations are kept for 90 days.',
  },
  ar: {
    open: 'فتح المساعدين الآليين',
    close: 'إغلاق',
    support: 'الدعم',
    market: 'السوق',
    supportName: 'MATT · مساعد الدعم الآلي',
    marketName: 'ANTHONY · مساعد السوق الآلي',
    aiNotice: 'أنت تتحدث مع ذكاء اصطناعي وليس مع شخص. الإجابات تُولَّد آلياً وقد تحتوي على أخطاء — تحقّق من المعلومات المهمة.',
    supportHello: 'مرحباً! يمكنني مساعدتك في استخدام Mid2Bid: تسجيل الدخول، الخطط، البحث، التنبيهات، إدارة العملاء، التقارير… بماذا أساعدك؟',
    marketHello: 'مرحباً! اسألني عن المناقصات والمشاريع والشركات وأخبار السوق في دول الخليج. أجيب فقط من قواعد بيانات Mid2Bid المتاحة لك ومن تقارير السوق لدينا.',
    placeholder: 'اكتب سؤالك…',
    send: 'إرسال',
    thinking: 'جارٍ التفكير…',
    leftDay: (n) => `متبقٍ اليوم: ${n}`,
    leftMonth: (n) => `متبقٍ هذا الشهر: ${n}`,
    records: 'السجلات',
    reports: 'تقارير السوق',
    owned: 'في مكتبتك',
    notOwned: 'متاح للشراء',
    upgrade: 'عرض الخطط',
    error: 'تعذّر على المساعد الإجابة. يرجى المحاولة بعد قليل.',
    privacy: 'تُحفظ المحادثات لمدة 90 يوماً.',
  },
};

const RECORD_PATH = { tenders: 'tenders', projects: 'projects', companies: 'companies', news: 'news' };

function Bubble({ m, c, isRTL }) {
  const mine = m.role === 'user';
  return (
    <div className={`flex ${mine ? (isRTL ? 'justify-start' : 'justify-end') : (isRTL ? 'justify-end' : 'justify-start')}`}>
      <div
        className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap break-words ${
          mine ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground'}`}
        dir="auto"
      >
        {m.content}
        {m.records && m.records.length > 0 && (
          <div className="mt-2 space-y-1">
            <p className="text-xs font-semibold opacity-80">{c.records}</p>
            {m.records.map((r) => (
              <Link
                key={`${r.type}-${r.id}`}
                to={`/dashboard/${RECORD_PATH[r.type] || r.type}/${pathId(r.id)}`}
                className="block text-xs underline underline-offset-2 hover:opacity-80"
                data-testid="chat-record-link"
              >
                {r.ref ? `[${r.ref}] ` : ''}{r.title}
              </Link>
            ))}
          </div>
        )}
        {m.reports && m.reports.length > 0 && (
          <div className="mt-2 space-y-1">
            <p className="text-xs font-semibold opacity-80">{c.reports}</p>
            {m.reports.map((r) => (
              <Link
                key={r.id}
                to={`/dashboard/reports/${pathId(r.id)}`}
                className="flex items-center gap-1 text-xs underline underline-offset-2 hover:opacity-80"
                data-testid="chat-report-link"
              >
                <FileText className="w-3 h-3 shrink-0" />
                <span>{r.title} — {r.owned ? c.owned : c.notOwned}</span>
              </Link>
            ))}
          </div>
        )}
        {m.upgrade && (
          <Link to={m.upgrade} className="block mt-2 text-xs font-semibold underline">{c.upgrade}</Link>
        )}
      </div>
    </div>
  );
}

export default function ChatAgents() {
  const { user, getToken } = useAuth();
  const { language, isRTL } = useLanguage();
  const c = COPY[language === 'ar' ? 'ar' : 'en'];
  const [state, setState] = useState(null);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('support');
  const [threads, setThreads] = useState({ support: [], market: [] });
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  const loadState = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/chat/state`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) { setState(null); return; }
      const s = await res.json();
      setState(s);
      setTab((t) => (s[t] && s[t].on ? t : (s.support.on ? 'support' : 'market')));
    } catch (e) {
      setState(null);
    }
  }, [getToken]);

  useEffect(() => { if (user) loadState(); }, [user, loadState]);
  useEffect(() => { if (endRef.current) endRef.current.scrollIntoView({ block: 'end' }); }, [threads, open, tab, busy]);

  if (!user || !state || (!state.support.on && !state.market.on)) return null;

  const agent = state[tab];
  const thread = threads[tab];
  const remainingText = agent && agent.remaining != null
    ? (agent.period === 'day' ? c.leftDay(agent.remaining) : c.leftMonth(agent.remaining))
    : '';

  const send = async () => {
    const msg = text.trim();
    if (!msg || busy) return;
    const which = tab;
    const history = threads[which].filter((m) => !m.local).map((m) => ({ role: m.role, content: m.content })).slice(-12);
    setThreads((t) => ({ ...t, [which]: [...t[which], { role: 'user', content: msg }] }));
    setText('');
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/chat/${which}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msg, history }),
      });
      const data = await res.json().catch(() => ({}));
      let reply;
      if (res.ok) {
        reply = { role: 'assistant', content: data.answer, records: data.records, reports: data.reports };
      } else {
        reply = {
          role: 'assistant', local: true,
          content: typeof data.detail === 'string' ? data.detail : c.error,
          upgrade: data.plan_gate ? data.plan_gate.upgrade_url : null,
        };
      }
      setThreads((t) => ({ ...t, [which]: [...t[which], reply] }));
    } catch (e) {
      setThreads((t) => ({ ...t, [which]: [...t[which], { role: 'assistant', local: true, content: c.error }] }));
    } finally {
      setBusy(false);
      loadState();
    }
  };

  const side = isRTL ? 'left-4' : 'right-4';

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={`fixed bottom-4 ${side} z-40 h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center hover:opacity-90`}
          aria-label={c.open}
          data-testid="chat-open"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}
      {open && (
        <div
          className={`fixed bottom-4 ${side} z-[48] w-[380px] max-w-[calc(100vw-2rem)] h-[560px] max-h-[calc(100dvh-6rem)] rounded-2xl border bg-card shadow-2xl flex flex-col`}
          dir={isRTL ? 'rtl' : 'ltr'}
          data-testid="chat-panel"
        >
          <div className="flex items-center justify-between gap-2 px-3 pt-3">
            <div className="flex gap-1">
              {['support', 'market'].filter((k) => state[k].on).map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setTab(k)}
                  className={`px-3 py-1 rounded-full text-sm ${tab === k ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}
                  data-testid={`chat-tab-${k}`}
                >
                  {k === 'support' ? c.support : c.market}
                </button>
              ))}
            </div>
            <Button variant="ghost" size="icon" onClick={() => setOpen(false)} aria-label={c.close} data-testid="chat-close">
              <X className="w-4 h-4" />
            </Button>
          </div>
          <div className="px-3 pt-2">
            <p className="flex items-center gap-1 text-sm font-semibold">
              <Bot className="w-4 h-4" /> {tab === 'support' ? c.supportName : c.marketName}
              <span className="ms-1 rounded bg-amber-500/20 px-1.5 text-[10px] font-bold text-amber-700 dark:text-amber-400">AI</span>
            </p>
            <p className="text-[11px] text-muted-foreground leading-snug mt-1" data-testid="chat-ai-notice">{c.aiNotice} {c.privacy}</p>
          </div>
          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2" data-testid="chat-messages">
            <Bubble m={{ role: 'assistant', content: tab === 'support' ? c.supportHello : c.marketHello }} c={c} isRTL={isRTL} />
            {thread.map((m, i) => <Bubble key={i} m={m} c={c} isRTL={isRTL} />)}
            {busy && (
              <p className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="w-3 h-3 animate-spin" /> {c.thinking}
              </p>
            )}
            <div ref={endRef} />
          </div>
          <div className="border-t p-2">
            <div className="flex items-end gap-2">
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value.slice(0, 1000))}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder={c.placeholder}
                rows={2}
                dir="auto"
                className="flex-1 resize-none rounded-xl border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                data-testid="chat-input"
              />
              <Button size="icon" onClick={send} disabled={busy || !text.trim()} aria-label={c.send} data-testid="chat-send">
                <Send className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
              </Button>
            </div>
            {remainingText && <p className="mt-1 text-[11px] text-muted-foreground" data-testid="chat-remaining">{remainingText}</p>}
          </div>
        </div>
      )}
    </>
  );
}
